'use client';

import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { fadeIn } from '@/lib/animations';
import { CheckCircle2, TrendingUp, Package } from 'lucide-react';
import { Button } from './components/ui/button';

export function TPLTailoredSolutions() {
	const [ref, inView] = useInView({
		triggerOnce: true,
		threshold: 0.1,
	});

	return (
		<section className="py-24 bg-white">
			<div className="container mx-auto px-4 md:px-6">
				<div className="grid lg:grid-cols-2 gap-12 items-center">
					<motion.div
						ref={ref}
						initial="hidden"
						animate={inView ? 'show' : 'hidden'}
						variants={fadeIn('right')}
					>
						<h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
							Tailored 3PL Solutions for Every Business
						</h2>

						<p className="text-lg text-gray-700 leading-relaxed mb-8">
							Every client's logistics needs are unique. That's why we never offer a
							one-size-fits-all service. Our approach is consultative - whether you
							need full-scale order fulfillment, specialized inventory management, or
							a combination of warehousing and distribution strategies, we design a
							solution that aligns with your business goals.
						</p>

						<div className="bg-brand-50 border border-brand-100 rounded-lg p-6 mb-8">
							<h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-3">
								<div className="w-8 h-8 bg-brand-100 rounded-full flex items-center justify-center text-brand-600">
									<TrendingUp size={18} />
								</div>
								Need help with a seasonal surge or long-term scalability?
							</h3>
							<p className="text-gray-700 leading-relaxed">
								Our 3PL solutions are built to handle volume spikes, product
								launches, and ongoing operations seamlessly.
							</p>
						</div>

						<div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
							<div className="flex items-center gap-3">
								<CheckCircle2 className="text-brand-600 flex-shrink-0" size={20} />
								<span className="text-gray-700">Consultative Approach</span>
							</div>
							<div className="flex items-center gap-3">
								<CheckCircle2 className="text-brand-600 flex-shrink-0" size={20} />
								<span className="text-gray-700">Scalable Solutions</span>
							</div>
							<div className="flex items-center gap-3">
								<CheckCircle2 className="text-brand-600 flex-shrink-0" size={20} />
								<span className="text-gray-700">Volume Spike Management</span>
							</div>
							<div className="flex items-center gap-3">
								<CheckCircle2 className="text-brand-600 flex-shrink-0" size={20} />
								<span className="text-gray-700">Product Launch Support</span>
							</div>
						</div>

						<Button
							asChild
							className="bg-brand-600 hover:bg-brand-700 text-white px-8 py-6 text-lg"
						>
							<a href="/contact">Discuss Your Needs</a>
						</Button>
					</motion.div>

					<motion.div
						ref={ref}
						initial="hidden"
						animate={inView ? 'show' : 'hidden'}
						variants={fadeIn('left')}
						className="relative"
					>
						<div className="absolute -top-6 -left-6 w-32 h-32 bg-brand-600 rounded-tl-2xl"></div>
						<img
							src="https://lindner-marketing.s3.eu-west-2.amazonaws.com/warehouse-worker-working-in-warehouse-storage-2025-03-14-23-53-47-utc.webp"
							alt="Warehouse worker in modern storage facility"
							className="rounded-lg shadow-xl relative z-10 w-full h-[500px] object-cover"
						/>
						<div className="absolute -bottom-6 -right-6 w-40 h-40 bg-brand-100 rounded-br-2xl"></div>
					</motion.div>
				</div>
			</div>
		</section>
	);
}
